import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, TextField, Link, Alert, Snackbar } from '@mui/material';
import {
  getAdvertLibrary,
  createAdvertLibraryItem,
  updateAdvertLibraryItem,
  deleteAdvertLibraryItem
} from '../../services/api';
import ConfirmDeleteDialog from '../../components/Common/ConfirmDeleteDialog';

const DropBox = ({ onFileSelected, file }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFileSelected(dropped);
  };

  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <Box
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        width: 150, height: 150, border: '2px solid #333',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', textAlign: 'center', p: 1,
        bgcolor: dragOver ? '#f0f0f0' : 'transparent',
        overflow: 'hidden'
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => e.target.files?.[0] && onFileSelected(e.target.files[0])}
      />
      {preview ? (
        <Box component="img" src={preview} alt="Preview" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
      ) : (
        <Typography variant="body2" fontWeight={600}>DROP FILE</Typography>
      )}
    </Box>
  );
};

const AdvertLibrary = () => {
  const { groupId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newFile, setNewFile] = useState(null);
  const [newLink, setNewLink] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editLink, setEditLink] = useState('');
  const [editFile, setEditFile] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdvertLibrary(groupId);
      if (res.success) setItems(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load advert library');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const handleLoad = async () => {
    if (!newFile && !newLink.trim()) {
      setError('An image or a link is required');
      return;
    }
    try {
      const res = await createAdvertLibraryItem(groupId, { file: newFile, link: newLink.trim() });
      setItems(prev => [...prev, res.data]);
      setNewFile(null);
      setNewLink('');
      setSuccess('Advert added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add advert');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditLink(item.link || '');
    setEditFile(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFile(null);
  };

  const saveEdit = async (item) => {
    try {
      await updateAdvertLibraryItem(groupId, item.id, { file: editFile, link: editLink.trim() });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        link: editLink.trim(),
        ...(editFile ? { image_url: URL.createObjectURL(editFile) } : {})
      } : i));
      setEditingId(null);
      setEditFile(null);
      setSuccess('Advert updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update advert');
    }
  };

  const requestRemove = (item) => setConfirmDelete(item);

  const handleConfirmDelete = async () => {
    const item = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteAdvertLibraryItem(groupId, item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setEditingId(null);
      setSuccess('Advert removed');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove advert');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Advert Library
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 1 }}>
        <DropBox file={newFile} onFileSelected={setNewFile} />
        <TextField
          size="small"
          placeholder="enter web link here"
          value={newLink}
          onChange={(e) => setNewLink(e.target.value)}
          sx={{ width: 320, mt: 0.5 }}
        />
        <Link component="button" underline="hover" onClick={handleLoad} sx={{ mt: 1.5 }}>Load</Link>
      </Box>
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        (Ideal file x=y with transparent background)
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>Loading...</Typography>
      ) : (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map((item) => {
            const isEditing = editingId === item.id;
            return (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.image_url && (
                    <Box component="img" src={item.image_url} alt="" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                  )}
                </Box>
                {isEditing ? (
                  <>
                    <TextField
                      size="small"
                      value={editLink}
                      onChange={(e) => setEditLink(e.target.value)}
                      sx={{ width: 320 }}
                    />
                    <Link component="button" underline="hover" onClick={() => saveEdit(item)}>Save</Link>
                    <Link component="button" underline="hover" onClick={cancelEdit}>Cancel</Link>
                    <Link component="button" underline="hover" color="error" onClick={() => requestRemove(item)}>Remove</Link>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ width: 320 }}>{item.link || '-'}</Typography>
                    <Link component="button" underline="hover" onClick={() => startEdit(item)}>Edit</Link>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdvertLibrary;
